<template>
  <Teleport to="body">
    <Transition name="editor-tutorial">
      <div
        v-if="isActive"
        :aria-label="$t('editor.tutorial.arialabel')"
        aria-modal="true"
        class="editor-tutorial bg-overlay-background inset-none"
        role="dialog"
        :style="cssVars"
      >
        <button
          :aria-label="$t('editor.tutorial.dismiss.arialabel')"
          class="editor-tutorial-dismiss inset-none"
          :disabled="!isFinished"
          type="button"
          @click="finish"
        />

        <Transition name="editor-tutorial-caption">
          <p
            v-if="caption"
            :key="caption.key"
            class="editor-tutorial-caption left-none right-none px-300 py-none text-center text-primary-primary-inverted"
            :data-direction="caption.direction"
            :style="{ top: `${captionTop}px` }"
          >
            <LcIcon class="editor-tutorial-arrow mb-050 block mx-auto" :name="caption.icon" />
            <span class="block headline-h5-headline">{{ caption.headline }}</span>
            <span class="block paragraph-regular">{{ caption.hint }}</span>
          </p>
        </Transition>

        <Transition name="editor-tutorial-button">
          <LcButton v-if="isFinished" class="editor-tutorial-button" @click="finish">
            {{ $t('editor.tutorial.button.gotit') }}
          </LcButton>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import type { LcIconName } from '@lottocom/frontend-components'

import type { EditorTutorialStep } from '../composables/editorTutorial'

import {
  EDITOR_TUTORIAL_ARROW_NUDGE_MS,
  EDITOR_TUTORIAL_ARROW_NUDGE_PX,
  EDITOR_TUTORIAL_BUTTON_BOTTOM_PX,
  EDITOR_TUTORIAL_BUTTON_FADE_MS,
  EDITOR_TUTORIAL_CAPTION_FADE_MS,
  EDITOR_TUTORIAL_CAPTION_GAP_PX,
  EDITOR_TUTORIAL_CAPTION_OFFSET_PX,
  EDITOR_TUTORIAL_OVERLAY_FADE_MS,
  EDITOR_TUTORIAL_OVERLAY_Z_INDEX,
  EDITOR_TUTORIAL_TARGET_ATTRIBUTE,
} from '../constants/tutorial'

type CaptionPreset = {
  direction: 'left' | 'right'
  icon: LcIconName
  key: string
}

const CAPTIONS: Partial<Record<EditorTutorialStep, CaptionPreset>> = {
  clear: { direction: 'left', icon: 'arrow-left', key: 'clear' },
  quickPick: { direction: 'right', icon: 'arrow-right', key: 'quickpick' },
}

const { t } = useI18n()
const { finish, isActive, isCaptionVisible, isFinished, step } = useEditorTutorial()

const isScrollLocked = useScrollLock(() => document.documentElement)

const captionTop = ref(0)

const cssVars = {
  '--editor-tutorial-button-bottom': `${EDITOR_TUTORIAL_BUTTON_BOTTOM_PX}px`,
  '--editor-tutorial-button-fade': `${EDITOR_TUTORIAL_BUTTON_FADE_MS}ms`,
  '--editor-tutorial-caption-fade': `${EDITOR_TUTORIAL_CAPTION_FADE_MS}ms`,
  '--editor-tutorial-caption-offset': `${EDITOR_TUTORIAL_CAPTION_OFFSET_PX}px`,
  '--editor-tutorial-nudge-distance': `${EDITOR_TUTORIAL_ARROW_NUDGE_PX}px`,
  '--editor-tutorial-nudge-duration': `${EDITOR_TUTORIAL_ARROW_NUDGE_MS}ms`,
  '--editor-tutorial-overlay-fade': `${EDITOR_TUTORIAL_OVERLAY_FADE_MS}ms`,
  '--editor-tutorial-z-index': String(EDITOR_TUTORIAL_OVERLAY_Z_INDEX),
}

const caption = computed(() => {
  if (!isCaptionVisible.value) return null

  const preset = CAPTIONS[step.value]
  if (!preset) return null

  return {
    ...preset,
    headline: t(`editor.tutorial.${preset.key}.headline`),
    hint: t(`editor.tutorial.${preset.key}.hint`),
  }
})

const updateCaptionPosition = () => {
  const target = document.querySelector(`[${EDITOR_TUTORIAL_TARGET_ATTRIBUTE}]`)
  if (!target) return

  captionTop.value = target.getBoundingClientRect().bottom + EDITOR_TUTORIAL_CAPTION_GAP_PX
}

watch(step, async () => {
  await nextTick()
  updateCaptionPosition()
})

watch(isActive, (active) => (isScrollLocked.value = active), { immediate: true })

useEventListener(window, ['resize', 'scroll'], updateCaptionPosition)

onBeforeUnmount(() => (isScrollLocked.value = false))
</script>

<style lang="scss" scoped>
.editor-tutorial {
  position: fixed;
  z-index: var(--editor-tutorial-z-index);
  backdrop-filter: token('shadow/blur/overlay/background/blur');
}

.editor-tutorial-dismiss {
  position: absolute;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
}

.editor-tutorial-caption {
  position: absolute;
  pointer-events: none;
}

.editor-tutorial-arrow {
  font-size: token('size/350');
}

.editor-tutorial-caption[data-direction='left'] .editor-tutorial-arrow {
  animation: editor-tutorial-nudge-left var(--editor-tutorial-nudge-duration) ease-in-out infinite;
}

.editor-tutorial-caption[data-direction='right'] .editor-tutorial-arrow {
  animation: editor-tutorial-nudge-right var(--editor-tutorial-nudge-duration) ease-in-out infinite;
}

.editor-tutorial-button {
  position: absolute;
  bottom: var(--editor-tutorial-button-bottom);
  left: 50%;
  transform: translateX(-50%);
}

@keyframes editor-tutorial-nudge-left {
  0%,
  100% {
    transform: translateX(0);
  }

  50% {
    transform: translateX(calc(-1 * var(--editor-tutorial-nudge-distance)));
  }
}

@keyframes editor-tutorial-nudge-right {
  0%,
  100% {
    transform: translateX(0);
  }

  50% {
    transform: translateX(var(--editor-tutorial-nudge-distance));
  }
}

.editor-tutorial-enter-active,
.editor-tutorial-leave-active {
  transition: opacity var(--editor-tutorial-overlay-fade) ease;
}

.editor-tutorial-enter-from,
.editor-tutorial-leave-to {
  opacity: 0;
}

.editor-tutorial-caption-enter-active,
.editor-tutorial-caption-leave-active {
  transition:
    opacity var(--editor-tutorial-caption-fade) ease,
    transform var(--editor-tutorial-caption-fade) ease;
}

.editor-tutorial-caption-enter-from,
.editor-tutorial-caption-leave-to {
  opacity: 0;
  transform: translateY(var(--editor-tutorial-caption-offset));
}

.editor-tutorial-button-enter-active {
  transition: opacity var(--editor-tutorial-button-fade) ease;
}

.editor-tutorial-button-enter-from {
  opacity: 0;
}
</style>
